import PlayerAvatar from "./avatar";
import styles from './playeravatar.module.css';
import { useDialogManager } from "../global/dialog";
import { useCallback, useMemo } from "react";
import { BOT_AVATAR } from "../../src/models/game/bot";
import PlayerProfileDialog from "../game/play/dialogs/playerprofiledialog";
import { ApiGame, ApiSeat } from "../../src/types/apigame";
import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useSession } from "next-auth/react";
import Game from "../../src/models/game";

export enum UserPlayerAvatarSize {
    Small = 0,
    Medium,
    MediumSmall,
}

type Props = {
    game?: ApiGame;
    hasGameStarted?: boolean;
    seatObject?: ApiSeat;
    showProfileOnClick?: boolean;
    disableAcceptedDimming?: boolean;
    showName?: boolean;
    size?: UserPlayerAvatarSize;
    showActiveTurn?: boolean;
};

export default function UserPlayerAvatar({
    game,
    hasGameStarted,
    seatObject,
    showProfileOnClick = true,
    disableAcceptedDimming = false,
    size,
    showActiveTurn = true,
    showName = false,
}: Props): JSX.Element {
    const { data: session, status } = useSession();
    const { addDialog } = useDialogManager();

    const myUserId = status === 'authenticated' && session.user.id;

    const classes: string[] = [styles.colorBox];

    if (
        (hasGameStarted && !seatObject?.isAlive) ||
        (!seatObject?.accepted && !disableAcceptedDimming)
    ) {
        classes.push(styles.isNotAlive);
    }

    if (
        showActiveTurn
        && game?.activeSeats?.some(
            (activeSeatObject) => activeSeatObject.id === seatObject?.id,
        )
    ) {
        classes.push(styles.turn);
    }

    if (size === UserPlayerAvatarSize.Medium) {
        classes.push(styles.medium);
    } else if (size === UserPlayerAvatarSize.MediumSmall) {
        classes.push(styles.mediumSmall);
    } else {
        classes.push(styles.small);
    }

    const userId = seatObject?.userId;

    let onClick = useCallback(
        (event: React.MouseEvent) => {
            if (!seatObject) {
                return;
            }

            addDialog(
                <PlayerProfileDialog
                    isOpen={true}
                    seatObject={seatObject}
                    onClose={() => { }}
                />,
            );

            event.stopPropagation();
        },
        [seatObject, addDialog],
    );

    const color = useMemo(
        () => {
            if (game && seatObject) {
                const g = new Game(game);
                return g.getColorForSeatObject(seatObject)?.hex;
            }

            return undefined;
        },
        [game, seatObject],
    );

    let avatarSrc: string;
    if (userId) {
        avatarSrc = seatObject.user?.avatar;
    } else if (seatObject?.bot?.id) {
        avatarSrc = BOT_AVATAR;
    } else {
        avatarSrc = null;
        classes.push(styles.emptySeat);
    }

    const mySeatObject = useMemo(
        () => game?.seats.find(
            (seatObjectIterator) => seatObjectIterator.userId === myUserId,
        ),
        [game, myUserId],
    );

    const playerAvatar = useMemo(
        () => {
            if (!userId && !avatarSrc && !mySeatObject) {
                return <Icon
                    className={styles.avatar}
                    icon={IconNames.Plus}
                />;
            } else if (!userId && !avatarSrc && mySeatObject) {
                return null;
            }

            return <PlayerAvatar
                className={styles.avatar}
                src={avatarSrc}
                title={seatObject.name}
            />
        },
        [avatarSrc, mySeatObject, seatObject, userId],
    );

    const nameComponent = useMemo(
        () => {
            if (!showName || !seatObject) {
                return null;
            }

            return <span style={{ marginLeft: '4px' }}>{seatObject.name}</span>;
        },
        [showName, seatObject],
    );

    return (
        <span onClick={showProfileOnClick ? onClick : null}>
            <div
                className={classes.join(' ')}
                style={{
                    backgroundColor: color,
                }}
            >
                {playerAvatar}
            </div>
            {nameComponent}
        </span>
    );
}
